import { AppStore } from 'app/state/store'
import { removeExportJob } from '../state/jobsExportingSlice'
import { Job, JobStatusType, JobType, isImportingJob, isProcessingJob } from '../types'
import { clearLocalLayerExportData } from 'features/builder/model/localExportsStore'

export function clearLayerExportJobs(store: AppStore) {
  store
    .getState()
    .jobsExporting.exports.filter(isClearable)
    .forEach((job) => {
      if (job.type === JobType.LocalLayerDataExport) {
        clearLocalLayerExportData(job.id)
      }
      store.dispatch(removeExportJob(job.id))
    })
}

export function getJobStatus(statusOrJob: JobStatusType | Job) {
  if (typeof statusOrJob === 'string') {
    return statusOrJob
  } else if (isProcessingJob(statusOrJob)) {
    return statusOrJob.status?.status || JobStatusType.Pending
  } else if (isImportingJob(statusOrJob)) {
    return statusOrJob.status
  } else {
    return statusOrJob.status
  }
}

export function getJobCreationTime(job: Job): number | undefined {
  if (isProcessingJob(job)) {
    return job.response?.createdAt ? new Date(job.response?.createdAt).getTime() : undefined
  } else if (isImportingJob(job)) {
    return job.createdAt ? new Date(job.createdAt).getTime() : undefined
  } else {
    return job.createdAt
  }
}

export function getNewestJob(jobs: Job[]) {
  return jobs.reduce((previous, job) => {
    const jobCreationTime = getJobCreationTime(job)
    if (jobCreationTime === undefined) return previous
    if (previous === undefined) return job

    const previousJobCreationtime = getJobCreationTime(previous)
    if (previousJobCreationtime !== undefined && previousJobCreationtime > jobCreationTime) return previous
    return job
  }, undefined as Job | undefined)
}

export function getSortedJobs(jobs: Job[]) {
  return [...jobs].sort((a, b) => {
    const ta = getJobCreationTime(a)
    const tb = getJobCreationTime(b)
    if (ta === undefined) return 1
    if (tb === undefined) return -1
    return tb - ta
  })
}

export function isClearable(statusOrJob: JobStatusType | Job) {
  const status = getJobStatus(statusOrJob)
  return status === JobStatusType.Failure || status === JobStatusType.Cancelled || status === JobStatusType.Success
}

export function isPendingOrRunning(statusOrJob: JobStatusType | Job) {
  const status = getJobStatus(statusOrJob)
  return status === undefined || status === JobStatusType.Pending || status === JobStatusType.Running
}

export function isFailed(statusOrJob: JobStatusType | Job) {
  const status = getJobStatus(statusOrJob)
  return status === JobStatusType.Failure
}

export function overallJobsStatus(jobs: Job[]) {
  const someJobsRunning = jobs.some(isPendingOrRunning)
  if (someJobsRunning) return JobStatusType.Running

  const someTaskFailed = jobs.some((task) => task.status === JobStatusType.Failure)
  if (someTaskFailed) return JobStatusType.Failure

  return JobStatusType.Success
}
